import compact from "lodash/compact";
import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {getChallengeScenariosLength, getMainScenario} from "../../../scenes/editor/challenge-edit.utils";

import {MAX_CHALLENGE_LENGTH, MAX_MAIN_SCENARIO_LENGTH} from "../../../settings";
import {Action, Scenario} from "../../../types";
import {ChallengeScenarioActivationMethod} from "../../../schema";
import {AsSpecificComponent} from "../../../ui";

import {AlertCircleIcon} from "../../../ui/icons";
import WithTooltip from "../../../ui/tooltip";

import classes from "./ChallengeWarnings.module.css";
import {circleInScenarios} from "./ChallengeWarnings.utils";

export type ChallengeWarningsProps = Omit<AsSpecificComponent<typeof WithTooltip, "div">, "helpText" | "children"> & {
  scenarios: Scenario[];
  rpgInstructionMissing?: boolean;
  rpgInstructionParsing?: boolean;
};

export default function ChallengeWarnings({scenarios, rpgInstructionMissing, rpgInstructionParsing,
                                            ...props}: ChallengeWarningsProps) {
  const {t} = useTranslation();

  const challengeLength = useMemo(() => getChallengeScenariosLength(scenarios), [scenarios]);

  const challengeHaveSecondaryScenario = scenarios.length > 1;

  const mainScenario = useMemo(() => getMainScenario(scenarios), [scenarios]);
  const mainScenarioLength = mainScenario.actions.length;

  const challengeOverflow = challengeLength - MAX_CHALLENGE_LENGTH;
  const mainScenarioOverflow = mainScenarioLength - MAX_MAIN_SCENARIO_LENGTH;

  const emptyActionsCount = useMemo(
    () => scenarios.reduce((count, scenario) => count + scenario.actions.filter(isActionEmpty).length, 0),
    [scenarios],
  );

  const circleDependenciesDanger = useMemo(() => circleInScenarios(scenarios), [scenarios]);

  const [emptyScenariosCount, emptyScenariosNames] = useMemo(() => {
    const empty = scenarios.filter((scenario) => scenario.actions.length === 0);
    return [empty.length, empty.map((scenario) => scenario.name)];
  }, [scenarios]);

  const [unusedScenariosCount, unusedScenariosNames] = useMemo(() => {
    const noTriggerPhrasesScenarios = scenarios.filter(
      (scenario) =>
        ((scenario.triggerPhrases.length === 0 &&
          scenario.activationMethod === ChallengeScenarioActivationMethod.TRIGGER_PHRASE) ||
          scenario.activationMethod !== ChallengeScenarioActivationMethod.TRIGGER_PHRASE) &&
        !scenario.isMain,
    );
    const actionsWithBranches = scenarios.flatMap((scenario) =>
      scenario.actions.map((action) => {
        if (action.__typename === "ChoiceUserInputAction") {
          return action;
        }
        return undefined;
      }),
    );
    const targetIds = actionsWithBranches.flatMap(
      (action) => action && action.branches.map((branch) => branch.targetScenarioId),
    );
    let count = 0;
    let names: string[] = [];
    noTriggerPhrasesScenarios.forEach((scenario) => {
      if (scenario && targetIds.filter((id) => id === scenario.id).length === 0) {
        count++;
        names.push(scenario.name);
      }
    });
    return [count, names];
  }, [scenarios]);

  const warningText = useMemo(() => {
    const warnings: (string | false)[] = [];

    function getActionPlural(count: number) {
      return t("types.plurals.action", {count});
    }

    function getScenarioPlural(count: number) {
      return t("types.plurals.scenario", {count});
    }

    if (circleDependenciesDanger.length > 0) {
      warnings.push(
        t("components.ChallengeWarnings.circularDependencies") + " (" + circleDependenciesDanger.join(", ") + ")",
      );
    }

    if (challengeHaveSecondaryScenario) {
      warnings.push(
        ...[
          challengeOverflow > 0 &&
            t("components.ChallengeWarnings.tooManyActions.challenge", {
              excess: getActionPlural(challengeOverflow),
            }),
          mainScenarioOverflow > 0 &&
            t("components.ChallengeWarnings.tooManyActions.mainScenario", {
              excess: getActionPlural(mainScenarioOverflow),
            }),
        ],
      );
    } else {
      warnings.push(
        ...[
          mainScenarioOverflow > 0 &&
            t("components.ChallengeWarnings.tooManyActions.challenge", {
              excess: getActionPlural(mainScenarioOverflow),
            }),
        ],
      );
    }

    if (emptyActionsCount) {
      warnings.push(
        ...[
          emptyActionsCount > 0 &&
            t("components.ChallengeWarnings.emptyActions", {
              excess: getActionPlural(emptyActionsCount),
            }),
        ],
      );
    }

    if (emptyScenariosCount > 0) {
      warnings.push(
        ...[
          emptyScenariosCount > 0 &&
            t("components.ChallengeWarnings.emptyScenarios", {
              excess: getScenarioPlural(emptyScenariosCount),
            }) +
              " (" +
              emptyScenariosNames.map((name) => name) +
              ")",
        ],
      );
    }

    if (unusedScenariosCount > 0) {
      warnings.push(
        ...[
          unusedScenariosCount > 0 &&
            t("components.ChallengeWarnings.unusedScenarios", {
              excess: getScenarioPlural(unusedScenariosCount),
            }) +
              " (" +
              unusedScenariosNames.map((name) => name) +
              ")",
        ],
      );
    }

    if (rpgInstructionMissing) {
      warnings.push(...[t("components.ChallengeWarnings.rpgInstructionMissing")]);
    }

    if (rpgInstructionParsing) {
      warnings.push(...[t("components.ChallengeWarnings.rpgInstructionParsing")]);
    }

    return compact(warnings)
      .map((item) => `<p>${item}</p>`)
      .join("\n");
  }, [
    t,
    challengeHaveSecondaryScenario,
    challengeOverflow,
    mainScenarioOverflow,
    emptyActionsCount,
    emptyScenariosCount,
    unusedScenariosCount,
    emptyScenariosNames,
    unusedScenariosNames,
    circleDependenciesDanger,
    rpgInstructionMissing,
    rpgInstructionParsing,
  ]);

  const iconClassName =
    circleDependenciesDanger.length > 0 || rpgInstructionMissing ? classes.danger : classes.warning;

  return (
    <>
      {warningText && (
        <WithTooltip helpText={warningText} {...props}>
          <AlertCircleIcon className={iconClassName} />
        </WithTooltip>
      )}
    </>
  );
}

function isActionEmpty(action: Action) {
  switch (action.__typename) {
    case "AvatarAction":
    case "SystemAction":
      return action.text === "";
    case "UserInputAction":
      return action.referenceText === "" || action.questionText === "";
    case "ChoiceUserInputAction":
      return (
        action.questionText === "" ||
        action.branches?.some((branch) => {
          if (branch.avatarReaction && isActionEmpty(branch.avatarReaction)) {
            return true;
          }
          if (branch.systemReaction && isActionEmpty(branch.systemReaction)) {
            return true;
          }
          if (branch.userInput && isActionEmpty(branch.userInput)) {
            return true;
          }
          return false;
        })
      );

    default:
      throw new Error(`isActionEmpty not implemented for type ${action.__typename}`);
  }
}
